import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import {
  Users,
  BookOpen,
  Layers,
  FileText,
  PenTool,
  GraduationCap,
  Video,
  TrendingUp,
  Award,
  Calendar,
  UserX,
  Clock,
  UsersRound
} from 'lucide-react'

interface DashboardStats {
  totalStudents: number
  totalTeachers: number
  totalUnits: number
  totalLessons: number
  totalContents: number
  totalExercises: number
  totalWorkshops: number
  activeStudents: number
  completedCourses: number
  pendingExams: number
  studentsWithoutCohort: number
}

interface RecentActivity {
  id: string
  type: 'enrollment' | 'exam_pass' | 'workshop' | 'certificate'
  description: string
  timestamp: string
  user_name: string
}

interface ActiveCohort {
  id: string
  name: string
  start_date: string
  end_date: string | null
  schedule_day: string | null
  schedule_start_time: string | null
  schedule_end_time: string | null
  student_ids: string[]
  max_students: number
  level_name: string | null
  teacher_name: string | null
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    totalStudents: 0,
    totalTeachers: 0,
    totalUnits: 0,
    totalLessons: 0,
    totalContents: 0,
    totalExercises: 0,
    totalWorkshops: 0,
    activeStudents: 0,
    completedCourses: 0,
    pendingExams: 0,
    studentsWithoutCohort: 0
  })
  const [_recentActivity, setRecentActivity] = useState<RecentActivity[]>([])
  const [activeCohorts, setActiveCohorts] = useState<ActiveCohort[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      // Cargar estadisticas
      const [
        { count: studentsCount },
        { count: teachersCount },
        { count: unitsCount },
        { count: lessonsCount },
        { count: contentsCount },
        { count: exercisesCount },
        { count: workshopsCount }
      ] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'student'),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'teacher'),
        supabase.from('units').select('*', { count: 'exact', head: true }),
        supabase.from('lessons').select('*', { count: 'exact', head: true }),
        supabase.from('contents').select('*', { count: 'exact', head: true }),
        supabase.from('exercises').select('*', { count: 'exact', head: true }),
        supabase.from('unit_workshops').select('*', { count: 'exact', head: true })
      ])

      // Cursos completados: estudiantes distintos que aprobaron el examen final
      let completedCount = 0
      try {
        const { data: passedFinal } = await supabase
          .from('final_exam_attempts')
          .select('student_id')
          .eq('passed', true)
        completedCount = new Set((passedFinal || []).map((r: any) => r.student_id)).size
      } catch { /* silencioso */ }

      // Examenes de unidad pendientes (alumno tiene la unidad lista para rendir)
      const { count: pendingExamsCount } = await supabase
        .from('student_unit_progress')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'exam_pending')

      // Alumnos activos: distintos con algún intento de examen en los últimos 30 días
      let activeCount = 0
      try {
        const monthAgo = new Date()
        monthAgo.setMonth(monthAgo.getMonth() - 1)
        const { data: recentAttempts } = await supabase
          .from('exam_attempts')
          .select('student_id')
          .gte('created_at', monthAgo.toISOString())
        activeCount = new Set((recentAttempts || []).map((r: any) => r.student_id)).size
      } catch { /* silencioso */ }

      // Estudiantes sin cohorte: obtener todos los student_ids de cohortes activas
      let studentsWithoutCohortCount = 0
      try {
        const [{ data: allStudents }, { data: allCohorts }] = await Promise.all([
          supabase.from('profiles').select('id').eq('role', 'student'),
          (supabase.from('cohorts') as any).select('student_ids').eq('is_active', true)
        ])
        const inCohort = new Set<string>(
          (allCohorts || []).flatMap((c: any) => c.student_ids || [])
        )
        studentsWithoutCohortCount = (allStudents || []).filter((s: any) => !inCohort.has(s.id)).length
      } catch {
        // silencioso si falla
      }

      setStats({
        totalStudents: studentsCount || 0,
        totalTeachers: teachersCount || 0,
        totalUnits: unitsCount || 0,
        totalLessons: lessonsCount || 0,
        totalContents: contentsCount || 0,
        totalExercises: exercisesCount || 0,
        totalWorkshops: workshopsCount || 0,
        activeStudents: activeCount || 0,
        completedCourses: completedCount || 0,
        pendingExams: pendingExamsCount || 0,
        studentsWithoutCohort: studentsWithoutCohortCount
      })

      // Cargar cohortes activas
      try {
        const { data: cohortsData } = await (supabase
          .from('cohorts') as any)
          .select('*')
          .eq('is_active', true)
          .order('start_date', { ascending: true })

        if (cohortsData && cohortsData.length > 0) {
          const levelIds = [...new Set(cohortsData.map((c: any) => c.level_id).filter(Boolean))]
          const teacherIds = [...new Set(cohortsData.map((c: any) => c.teacher_id).filter(Boolean))]

          const [levelsRes, teachersRes] = await Promise.all([
            levelIds.length > 0
              ? (supabase.from('levels') as any).select('id, name').in('id', levelIds)
              : { data: [] },
            teacherIds.length > 0
              ? supabase.from('profiles').select('id, first_name, last_name').in('id', teacherIds)
              : { data: [] }
          ])

          const levelsMap = new Map((levelsRes.data || []).map((l: any) => [l.id, l.name]))
          const teachersMap = new Map((teachersRes.data || []).map((t: any) => [t.id, `${t.first_name} ${t.last_name}`]))

          setActiveCohorts(cohortsData.map((c: any) => ({
            id: c.id,
            name: c.name,
            start_date: c.start_date,
            end_date: c.end_date,
            schedule_day: c.schedule_day,
            schedule_start_time: c.schedule_start_time,
            schedule_end_time: c.schedule_end_time,
            student_ids: c.student_ids || [],
            max_students: c.max_students,
            level_name: levelsMap.get(c.level_id) || null,
            teacher_name: teachersMap.get(c.teacher_id) || null
          })))
        }
      } catch { /* silencioso */ }

      // Cargar actividad reciente
      const { data: activities } = await supabase
        .from('exam_attempts')
        .select(`
          id,
          score,
          passed,
          created_at,
          profiles:user_id (first_name, last_name)
        `)
        .order('created_at', { ascending: false })
        .limit(5)

      if (activities) {
        setRecentActivity(
          activities.map((a: any) => ({
            id: a.id,
            type: a.passed ? 'exam_pass' : 'enrollment',
            description: a.passed
              ? `Aprobo examen con ${a.score}%`
              : `Intento de examen: ${a.score}%`,
            timestamp: a.created_at,
            user_name: `${a.profiles?.first_name || ''} ${a.profiles?.last_name || ''}`
          }))
        )
      }
    } catch (error) {
      console.error('Error loading dashboard:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const statCards = [
    { label: 'Estudiantes', value: stats.totalStudents, icon: Users, color: 'bg-[#0055A4]/50', href: '/admin/users' },
    { label: 'Profesores', value: stats.totalTeachers, icon: GraduationCap, color: 'bg-purple-500', href: '/admin/users' },
    { label: 'Unidades', value: stats.totalUnits, icon: Layers, color: 'bg-green-500', href: '/admin/units' },
    { label: 'Lecciones', value: stats.totalLessons, icon: BookOpen, color: 'bg-yellow-500', href: '/admin/lessons' },
    { label: 'Contenidos', value: stats.totalContents, icon: FileText, color: 'bg-orange-500', href: '/admin/contents' },
    { label: 'Ejercicios', value: stats.totalExercises, icon: PenTool, color: 'bg-pink-500', href: '/admin/exercises' },
    { label: 'Talleres', value: stats.totalWorkshops, icon: Video, color: 'bg-indigo-500', href: '/admin/workshops' },
    { label: 'Alumnos sin Cohorte', value: stats.studentsWithoutCohort, icon: UserX, color: 'bg-teal-500', href: '/admin/users?filter=no-cohort' }
  ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard de Administracion</h1>
        <p className="text-gray-600">Vista general del sistema</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Link
            key={stat.label}
            to={stat.href}
            className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Quick Actions & Activity */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Acciones Rapidas</h2>
          <div className="grid grid-cols-2 gap-3">
            <Link
              to="/admin/units/new"
              className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-green-700 hover:bg-green-100 transition-colors"
            >
              <Layers className="w-5 h-5" />
              <span className="text-sm font-medium">Nueva Unidad</span>
            </Link>
            <Link
              to="/admin/lessons/new"
              className="flex items-center gap-2 p-3 bg-[#0055A4]/5 rounded-lg text-[#0055A4] hover:bg-[#0055A4]/10 transition-colors"
            >
              <BookOpen className="w-5 h-5" />
              <span className="text-sm font-medium">Nueva Lección</span>
            </Link>
            <Link
              to="/admin/contents/new"
              className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg text-orange-700 hover:bg-orange-100 transition-colors"
            >
              <FileText className="w-5 h-5" />
              <span className="text-sm font-medium">Nuevo Contenido</span>
            </Link>
            <Link
              to="/admin/exercises/new"
              className="flex items-center gap-2 p-3 bg-pink-50 rounded-lg text-pink-700 hover:bg-pink-100 transition-colors"
            >
              <PenTool className="w-5 h-5" />
              <span className="text-sm font-medium">Nuevo Ejercicio</span>
            </Link>
            <Link
              to="/admin/exams/new"
              className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg text-purple-700 hover:bg-purple-100 transition-colors"
            >
              <GraduationCap className="w-5 h-5" />
              <span className="text-sm font-medium">Nuevo Examen</span>
            </Link>
            <Link
              to="/admin/workshops/new"
              className="flex items-center gap-2 p-3 bg-indigo-50 rounded-lg text-indigo-700 hover:bg-indigo-100 transition-colors"
            >
              <Video className="w-5 h-5" />
              <span className="text-sm font-medium">Nuevo Taller</span>
            </Link>
          </div>
        </div>

        {/* Cohortes Activas */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Cohortes Activas</h2>
            <Link
              to="/admin/cohorts"
              className="text-sm text-[#0055A4] hover:underline"
            >
              Ver todas
            </Link>
          </div>
          {activeCohorts.length > 0 ? (
            <div className="space-y-3">
              {activeCohorts.map((cohort) => (
                <Link
                  key={cohort.id}
                  to={`/admin/cohorts/${cohort.id}`}
                  className="block p-3 rounded-lg border border-gray-100 hover:border-[#0055A4]/20 hover:bg-[#0055A4]/[0.02] transition-colors"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <h3 className="font-medium text-gray-900 text-sm">{cohort.name}</h3>
                    {cohort.level_name && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#0055A4]/10 text-[#0055A4]">
                        {cohort.level_name}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1">
                    {cohort.schedule_day && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        <span>
                          {cohort.schedule_day}
                          {cohort.schedule_start_time && cohort.schedule_end_time
                            ? ` · ${cohort.schedule_start_time.slice(0, 5)} - ${cohort.schedule_end_time.slice(0, 5)}`
                            : ''}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-xs text-gray-600">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      <span>
                        {new Date(cohort.start_date + 'T12:00:00').toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}
                        {cohort.end_date && ` — ${new Date(cohort.end_date + 'T12:00:00').toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <UsersRound className="w-3.5 h-3.5 text-gray-400" />
                        <span>{cohort.student_ids.length} / {cohort.max_students} alumnos</span>
                      </div>
                      {cohort.teacher_name && (
                        <span className="text-xs text-gray-500">{cohort.teacher_name}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <UsersRound className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No hay cohortes activas</p>
            </div>
          )}
        </div>
      </div>

      {/* Progress Overview */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-green-100">
              <Award className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.completedCourses}</p>
              <p className="text-sm text-gray-500">Cursos Completados</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Estudiantes que aprobaron el examen final
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-yellow-100">
              <Calendar className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingExams}</p>
              <p className="text-sm text-gray-500">Examenes Pendientes</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Estudiantes esperando para tomar examen
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-[#0055A4]/10">
              <TrendingUp className="w-6 h-6 text-[#0055A4]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {stats.activeStudents}
              </p>
              <p className="text-sm text-gray-500">Activos (30d)</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            Alumnos con algún examen rendido en los últimos 30 días
          </p>
        </div>
      </div>
    </div>
  )
}
